// 最小警報處理（SPEC EVT-06 #vms-web-alarm-handling，W25）：level = alarm 的事件有一個全家戶 / 場域共用的處理狀態
//   open（未處理）→ acknowledged（已看到）→ claimed（我來處理）→ resolved（已處理）| false_alarm（誤報），結案可重開。
// 每次變更可附一行備註（≤ 200 字）；帶畫面看到的 expected_version（plan#36：換人接手 / 重開回原狀態也辨識得出）與 expected_state，
// 對不上 → 409 ALARM_STATE_CONFLICT（detail.current / by_name / alarm），畫面提示並重畫。
// 變更經 /v1/vms/stream 的 alarm_state 幀推給同範圍的人（lib/alarmStates）。
import { KinioError, kinioFetch, planeBase, type AlarmStateFrame } from '@kinio/web-sdk';

export type AlarmStateName = 'open' | 'acknowledged' | 'claimed' | 'resolved' | 'false_alarm';

export const ALARM_STATES: AlarmStateName[] = ['open', 'acknowledged', 'claimed', 'resolved', 'false_alarm'];

/** 備註上限（字元，一行）。 */
export const ALARM_NOTE_MAX = 200;

export interface AlarmInfo {
  state: AlarmStateName;
  claimed_by?: string;
  claimed_by_name?: string;
  note?: string;
  updated_by?: string;
  updated_by_name?: string;
  updated_at?: string;
  /** 每次變更 +1（舊平台沒帶 → undefined） */
  version?: number;
  /** 指派（EVT-07，SPEC #vms-web-alarm-workbench）：被指派的人、誰指派、什麼時候；沒有指派不帶 */
  assigned_to?: string;
  assigned_to_name?: string;
  assigned_by?: string;
  assigned_by_name?: string;
  assigned_at?: string;
  /** 重開的時刻（接手時限從這裡重新起算）；沒重開過不帶 */
  sla_from?: string;
}

/** 接手時限（EVT-07）：清單每筆帶，沒有時限不帶。group = 安防類 / 狀態類；source = 設定來源。 */
export interface AlarmSla {
  minutes: number;
  group: 'security' | 'status';
  started_at: string;
  due_at: string;
  source: { kind: 'org' | 'site' | 'share'; org_id: string; site_id?: string };
  notified_at?: string;
}

export interface AlarmItem {
  event_id: string;
  home_id: string;
  device_ref: string;
  class: string;
  key: string;
  occurred_at: string;
  has_clip: boolean;
  alarm: AlarmInfo;
  /** started / ended（platform 2571097e 起每筆帶，沒有 phase 的事件不帶；舊平台沒帶時網頁用全文檢索補，lib/alarmPhases，第九輪 B9-01） */
  phase?: string;
  binding_id?: string;
  /**
   * 這一筆算不算一次事件（同全文檢索的 counted：同一次的開始＋結束只有一筆是 true；沒有 phase 的都是 true）。
   * 第九輪平台收尾起每筆都帶（SPEC #vms-web-909 alarms 那列）；舊平台沒有這個欄位 → undefined。
   */
  counted?: boolean;
  /** 接手時限（EVT-07）；沒有時限 / 舊平台不帶 */
  sla?: AlarmSla;
}

export type AlarmChangeKind = 'state' | 'assign' | 'unassign' | 'note' | 'overdue';

export interface AlarmChange {
  event_id: string;
  from: AlarmStateName;
  to: AlarmStateName;
  by: string;
  by_name?: string;
  note?: string;
  at: string;
  version?: number;
  /** EVT-07：state / assign / unassign / note / overdue（舊平台沒帶 = state） */
  kind?: AlarmChangeKind;
  /** assign：新的被指派者；unassign：原被指派者 */
  assignee?: string;
  assignee_name?: string;
  /** 別人接手 / 重開時清掉的原被指派者 */
  cleared_assignee?: string;
  cleared_assignee_name?: string;
  /** overdue：逾時通知送給了誰；merged_into：併入那一則逾時通知（沒另發） */
  notified?: { user_id: string; name?: string }[];
  merged_into?: string;
}

export interface AlarmAssignee {
  user_id: string;
  display_name: string;
  is_me: boolean;
}

export interface AlarmList {
  alarms: AlarmItem[];
  next_cursor?: string;
  /**
   * open + acknowledged（同樣的設備範圍）。第九輪平台收尾起是**事件次數**（只數 counted 的列）；舊平台是紀錄筆數（eventCounts = false）。
   */
  unhandled: number;
  /** false = 只能看（guest） */
  can_handle: boolean;
  /** 平台每筆帶 counted（第九輪平台收尾）：unhandled 是事件次數、每筆有 phase / binding_id，不必用全文檢索補（清單空的也當是，空清單 unhandled 一定是 0） */
  eventCounts: boolean;
}

/** 這份清單是不是新平台的形狀（每筆帶 counted）。空清單 → true（沒有東西要補，unhandled 也是 0）。 */
export function hasEventCounts(alarms: Pick<AlarmItem, 'counted'>[]): boolean {
  return alarms.length === 0 || alarms.some((a) => typeof a.counted === 'boolean');
}

function alarmsUrl(api: string, homeId: string, path = ''): string {
  return `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/vms/alarms${path}`;
}

export const alarmApi = {
  list: (api: string, homeId: string, p: { state?: AlarmStateName[]; cursor?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (p.state?.length) qs.set('state', p.state.join(','));
    if (p.cursor) qs.set('cursor', p.cursor);
    if (p.limit) qs.set('limit', String(p.limit));
    return kinioFetch<Omit<AlarmList, 'eventCounts'>>(alarmsUrl(api, homeId, qs.size ? '?' + qs : '')).then((r): AlarmList => {
      const alarms = Array.isArray(r?.alarms) ? r.alarms : [];
      return { alarms, next_cursor: r?.next_cursor, unhandled: r?.unhandled ?? 0, can_handle: !!r?.can_handle, eventCounts: hasEventCounts(alarms) };
    });
  },
  get: (api: string, homeId: string, eventId: string) =>
    kinioFetch<{ alarm: AlarmInfo & { event_id: string }; history: AlarmChange[]; can_handle: boolean; sla?: AlarmSla }>(alarmsUrl(api, homeId, '/' + encodeURIComponent(eventId))),
  /** 指派（EVT-07）：assignee null = 取消指派 */
  assign: (api: string, homeId: string, eventId: string, body: { assignee: string | null; expected_version: number; note?: string }) =>
    kinioFetch<{ alarm: AlarmInfo; change: AlarmChange }>(alarmsUrl(api, homeId, '/' + encodeURIComponent(eventId) + '/assign'), { method: 'POST', body }),
  /** 只加備註（狀態不變） */
  note: (api: string, homeId: string, eventId: string, body: { note: string; expected_version: number }) =>
    kinioFetch<{ alarm: AlarmInfo; change: AlarmChange }>(alarmsUrl(api, homeId, '/' + encodeURIComponent(eventId) + '/note'), { method: 'POST', body }),
  /** 能指派給誰（此刻能處理這台的人；我在最前） */
  assignees: (api: string, homeId: string, eventId: string) =>
    kinioFetch<{ assignees: AlarmAssignee[]; truncated: boolean }>(alarmsUrl(api, homeId, '/' + encodeURIComponent(eventId) + '/assignees')),
  setState: (api: string, homeId: string, eventId: string, body: { state: AlarmStateName; expected_state: AlarmStateName; expected_version: number; note?: string }) =>
    kinioFetch<{ alarm: AlarmInfo; change: AlarmChange }>(alarmsUrl(api, homeId, '/' + encodeURIComponent(eventId) + '/state'), { method: 'POST', body }),
};

/** 選單的未處理數算哪些。 */
export function isUnhandled(s: AlarmStateName | undefined): boolean {
  return s === undefined || s === 'open' || s === 'acknowledged';
}

/** 這個狀態下能按哪些（〔已看到〕〔我來處理〕〔已處理〕〔誤報〕，結案的只有〔重開〕）。 */
export function actionsFor(info: AlarmInfo | undefined, me: string): AlarmStateName[] {
  switch (info?.state ?? 'open') {
    case 'open':
      return ['acknowledged', 'claimed', 'false_alarm'];
    case 'acknowledged':
      return ['claimed', 'false_alarm'];
    case 'claimed':
      // 別人在處理：可以接手；自己在處理：結案
      return info?.claimed_by && info.claimed_by !== me ? ['claimed', 'resolved', 'false_alarm'] : ['resolved', 'false_alarm'];
    default:
      return ['open'];
  }
}

/** alarm_state 幀 → 目前狀態。 */
export function infoFromFrame(f: AlarmStateFrame): AlarmInfo {
  return {
    state: f.state as AlarmStateName,
    claimed_by: f.state === 'claimed' ? f.by : undefined,
    claimed_by_name: f.state === 'claimed' ? f.byName : undefined,
    note: f.note,
    updated_by: f.by,
    updated_by_name: f.byName,
    updated_at: f.at,
    version: f.version,
  };
}

/** 409 ALARM_STATE_CONFLICT 的目前狀態（伺服器帶的 detail.alarm）；不是衝突回 null。 */
export function conflictOf(e: unknown): { current: AlarmInfo; byName: string } | null {
  if (!(e instanceof KinioError) || e.code !== 'ALARM_STATE_CONFLICT') return null;
  const d = e.detail ?? {};
  const cur = (d.alarm as AlarmInfo | undefined) ?? { state: (d.current as AlarmStateName) ?? 'open' };
  return { current: cur, byName: String(d.by_name || d.claimed_by_name || d.by || '') };
}

/** 備註：一行、≤ 200 字（跟伺服器同一條；換行一律不收）。 */
export function noteOK(note: string): boolean {
  return [...note.trim()].length <= ALARM_NOTE_MAX && !/[\r\n]/.test(note);
}
